import React, {useContext, useState, useEffect} from "react";
import styled from "styled-components";
import moment from "moment";
import {Card, Text, Box, Flex, Button} from "rebass";
import {Input} from "@rebass/forms";
import {useHistory} from "react-router-dom";
import {useWindowWidth, useWindowHeight} from "@react-hook/window-size";
import {PatientsContext, TourContext} from "../../Contexts";
import {Patient} from "../../Types";
import {AddPatientModal, EditPatientModal} from "./AddPatientModal";
import {HomeActivationText, HomeIcon, LabeledText, Modal, SelectHeader} from "../../Components";
import axios from "axios";
import {EditIcon} from "../../Components/Icons/EditIcon";

const PatientCard: React.FC<{
  patient: Patient;
  selected: boolean;
  onCardClick: (e?) => void;
  button?: JSX.Element;
  dataTour?: string
}> = ({patient, selected, onCardClick, button, dataTour=""}) => {

  const displayName = patient.user_name.length < 30? patient.user_name : patient.user_name.slice(0, 30) + "..."

  return (
    <div
      onClick={(e) => {
        if (e.target["getAttribute"]("class") === "svg_button") {
          return
        }
        onCardClick(e)
      }}
      id={"card_" + patient.clinic_user_id}
    >
      <Card variant={selected ? "selectedCard" : "selectableCard"} data-tour={dataTour}>
        <Flex justifyContent="space-between" alignContent="flax-start">
          <Text
            fontWeight="bold"
            fontSize={3}
            sx={{
              overflowWrap: "break-word",
              wordWrap: "break-word",
              hyphens: "auto",
            }}
            paddingRight={2}
          >
            {displayName}
          </Text>
          {button}
        </Flex>

        <Flex paddingTop={1}>
          <LabeledText label={"Patient ID:"} value={'#' + patient.clinic_user_id} paddingLeft={1} paddingRight={3}/>
        </Flex>

        <Flex paddingTop={1}>
          <LabeledText label={"DOB:"} value={moment.unix(+patient.dob).format("YYYY/MM/DD")} paddingLeft={1}
                       paddingRight={3}/>
          {patient.home_status === "Active" && (
            <HomeActivationText home_activation_code={patient.home_activation_code}/>
          )}
        </Flex>



        <Flex paddingTop={1}>
          <LabeledText label={"Last Activity:"}
                       value={+patient.last_played !== 0 ? moment.unix(+patient.last_played).fromNow() : "Never"}
                       paddingLeft={1} paddingRight={3}/>
        </Flex>
      </Card>
    </div>
  );
};

export const PatientSelectSideBar: React.FC<{
  selectedPatient: Patient | undefined;
}> = ({selectedPatient}) => {
  const history = useHistory();
  const patientsContext = useContext(PatientsContext);
  const tourContext = useContext(TourContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [addModalIsOpen, setAddModalIsOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | undefined>();
  const [archivedModalIsOpen, setArchivedModalIsOpen] = useState(false);

  const filteredPatients = patientsContext.patients.filter((p) => {
    return (
      p.is_active === "1"  && searchFor(p, searchTerm)
    );
  });

  return (
    <CollapsibleCard>
      <Flex
        sx={{
          width: "320px",
          height: "100%",
          borderRight: "solid 1px #e7e7e7",
          background: "white",
          zIndex: tourContext.selectedElement === '[data-tour="activePatientsList"]'? "1000":"0"
        }}
        flexDirection="column"
      >
        <SelectHeader/>

        <Box padding={3} paddingTop={0} paddingBottom={0}>
          <Text fontWeight="bold" fontSize={4}>
            Active Patients
          </Text>
          <Input
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            placeholder="Search (Name, ID, DOB)"
          />
        </Box>
        <Text paddingLeft={3} paddingBottom={1} fontSize={0}>
          Total: {filteredPatients.length}/{patientsContext.patients.length}
        </Text>

        <PatientList>
          {filteredPatients.map((p) => (
            <PatientCard
              key={p.clinic_user_id}
              patient={p}
              selected={selectedPatient?.clinic_user_id === p.clinic_user_id}
              onCardClick={() => {
                const path = history.location.pathname.split('/')
                const previous_clinic_id = path[2]
                if (p.clinic_user_id !== previous_clinic_id) {
                  const tab = path[3] !== undefined ? "/" + path[3] : ""
                  history.push(`/patient/${p.clinic_user_id}` + '/configuration');
                }
              }}
              button={
                <EditIcon size="27" onClick={() => {setEditingPatient(p)}}/>
              }
              dataTour={selectedPatient?.clinic_user_id === p.clinic_user_id? "selectedPatient" : ""}
            />
          ))}
        </PatientList>

        <Button
          onClick={() => {
            setAddModalIsOpen(true);
            tourContext.callbacks["onOpenAddPatient"]()
          }}
          margin={3}
          marginTop={3}
          marginBottom={1}
          data-tour="addPatientButton"
        >
          Add Patient
        </Button>
        <Button
          onClick={() => {
            setArchivedModalIsOpen(true);
            tourContext.callbacks["onOpenArchive"]()
          }}
          margin={3}
          marginTop={1}
          data-tour="openArchiveButton"
        >
          Archive and Restore Patients
        </Button>
      </Flex>
      <AddPatientModal isOpen={addModalIsOpen} setIsOpen={setAddModalIsOpen}/>
      <EditPatientModal patient={editingPatient} setPatient={setEditingPatient}/>
      <ArchivedPatientsModal setIsOpen={setArchivedModalIsOpen} IsOpen={archivedModalIsOpen}/>
    </CollapsibleCard>
  );
};

const PatientList: React.FC<{}> = ({children}) => {
  return <div style={{height: useWindowHeight() - 330 + "px", overflow: "auto"}}>{children}</div>
}

const CollapsibleCard: React.FC<{}> = ({children}) => {
  const width = useWindowWidth();

  const [collapsedMode, setCollapsedMode] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const tourContext = useContext(TourContext);

  useEffect(() => {
    if (width > 900) {
      setCollapsedMode(false);
      setIsCollapsed(false);
    } else {
      setCollapsedMode(true);
      setIsCollapsed(true);
    }
  }, [width]);

  return (
    <Flex
      sx={{
        width: "400px",
        position: collapsedMode ? "absolute" : "relative",
        background: "white",
        left: isCollapsed ? "-250px" : "0px",
        alignItems: "center"
      }}
      data-tour="activePatientsList"
    >
      {children}
      {collapsedMode && (
        <Box
          onClick={() => {
            setIsCollapsed(!isCollapsed);
          }}
          sx={{
            width: "40px",
            backgroundColor: "white",
            writingMode: "vertical-rl",
            textAlign: "center",
            boxShadow: "card",
            height: "150px",
            lineHeight: "40px",
            borderRadius: "default",
          }}
        >
          Select Patient
        </Box>
      )}
    </Flex>
  );
};

const searchFor: (Patient, string) => boolean = (patient, searchTerm)  => {
  searchTerm = searchTerm.toLowerCase().replaceAll("#", "")

  return (
    !searchTerm
    || patient.user_name.toLowerCase().includes(searchTerm)
    || patient.clinic_user_id.toLowerCase().includes(searchTerm)
    || moment.unix(+patient.dob).format("YYYY/MM/DD").includes(searchTerm)
  )
}

export const ArchivedPatientsModal: React.FC<{
  IsOpen: any;
  setIsOpen: (isOpen: boolean) => void;
}> = ({IsOpen, setIsOpen}) => {
  const patientsContext = useContext(PatientsContext);
  const tourContext = useContext(TourContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [lastMoved, setLastMoved] = useState("");

  const activePatients = patientsContext.patients.filter((p) => {
    return (
      p.is_active === "1" && searchFor(p, searchTerm)
    );
  });

  const archivedPatients = patientsContext.patients.filter((p) => {
    return (
      p.is_active === "0"  && searchFor(p, searchTerm)
    );
  });

  const tourPatient = patientsContext.patients.find(p => p.clinic_user_id.slice(0, 4) === "fake")

  return (
    <Modal isOpen={IsOpen} setIsOpen={setIsOpen}>
      <Flex>
        <Flex
          sx={{
            width: "400px",
            height: "100%",
            borderRight: "solid 1px #e7e7e7",
            background: "white",
          }}
          padding={3}
          paddingRight={4}
          flexDirection="column"
        >
          <Box padding={3} paddingTop={0} paddingBottom={0}>
            <Text fontWeight="bold" fontSize={4}>
              Active Patients
            </Text>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              placeholder="Search (Name, ID, DOB)"
            />
          </Box>
          <Text paddingLeft={3} paddingBottom={1} fontSize={0}>
            Total: {activePatients.length}/{patientsContext.patients.length}
          </Text>

          <PatientList>
            {activePatients.map((p) => (
              <PatientCard
                key={p.clinic_user_id}
                patient={p}
                selected={p.clinic_user_id === lastMoved}
                onCardClick={() => {}}
                button={
                  <img className="svg_button" src="/ui/delete.svg" alt="delete"
                       style={{
                         width: "26px"
                       }}
                       onClick={() => {
                         patientsContext.archivePatient(p)
                         setLastMoved(p.clinic_user_id)
                         tourContext.callbacks["onPatientArchived"]()
                       }}
                  />
                }
                dataTour={(p.clinic_user_id === (tourPatient?.clinic_user_id || activePatients[0].clinic_user_id)? "archivePatientCard":"")}
              />
            ))}
          </PatientList>
        </Flex>
        <Flex
          sx={{
            width: "400px",
            height: "100%",
            background: "white",
          }}
          padding={3}
          paddingRight={4}
          flexDirection="column"
        >
          <Box padding={3} paddingTop={0} paddingBottom={0}>
            <Text fontWeight="bold" fontSize={4}>
              Archived Patients
            </Text>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              placeholder="Search (Name, ID, DOB)"
            />
          </Box>
          <Text paddingLeft={3} paddingBottom={1} fontSize={0}>
            Total: {archivedPatients.length}/{patientsContext.patients.length}
          </Text>

          <PatientList>
            {archivedPatients.map((p) => (
              <PatientCard
                key={p.clinic_user_id}
                patient={p}
                selected={p.clinic_user_id === lastMoved}
                onCardClick={() => {}}
                button={
                  <img className="svg_button" src="/ui/save.svg" alt="delete"
                       style={{
                         width: "26px"
                       }}
                       onClick={() => {
                         patientsContext.restorePatient(p)
                         if (tourContext.openedTour !== null) setIsOpen(false)
                         setLastMoved(p.clinic_user_id)
                         document.getElementById("card_" + p.clinic_user_id)?.scrollIntoView()
                         tourContext.callbacks["onPatientRestored"]()
                       }}
                  />
                }
                dataTour={(p.clinic_user_id === lastMoved? "restorePatientCard":"")}
              />
            ))}
          </PatientList>
        </Flex>
      </Flex>
    </Modal>
  );
};
